import * as Yup from "yup";
import * as _ from "lodash";
import { ManagementMessages } from "@shared/languages/interfaces";

/**
 * Generates a Yup validation schema for user input validation.
 *
 * @param {function(string): string} t - Translation function to retrieve localized validation messages.
 * @param {number} activeOptionsPlantsLength - Number of selected plant-related options. Used to validate plant selection.
 * @param {string} [emailError] - Optional custom validation message for the email field if an error exists.
 * @returns {object} Yup validation schema configured with rules for user fields including name, email, plants, password, and password confirmation.
 */
export const getUserValidationSchema = (
  t: (key: string) => string,
  activeOptionsPlantsLength: number,
  emailError?: string
) => {
  return Yup.object().shape({
    name: Yup.string().trim().required(t(ManagementMessages.formRequiredName)),
    email: Yup.string()
      .trim()
      .required(t(ManagementMessages.formRequiredEmail))
      .email(t(ManagementMessages.formValidEmail))
      .test(
        "validateEmail",
        emailError,
        () => !emailError || emailError.length === 0
      ),
    plants: Yup.string().test(
      "hasPlants",
      t(ManagementMessages.formRequiredPlant),
      () => activeOptionsPlantsLength !== 0
    ),
    password: Yup.string()
      .trim()
      .test(
        "validatePassword",
        t(ManagementMessages.formValidPassword),
        (val: string | undefined) => _.isEmpty(val) || (val?.length || 0) >= 6
      ),
    passwordConfirm: Yup.string()
      .trim()
      .oneOf([Yup.ref("password"), null], t(ManagementMessages.formEqualsPassword))
      .test(
        "validatePasswordConfirm",
        t(ManagementMessages.formValidConfirmPassword),
        (val: string | undefined) => _.isEmpty(val) || (val?.length || 0) >= 6
      )
  });
};
